/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { Instances } from '../../../../ts/instance';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import { listInstances } from '../../api/cloud';
import { selectPlanId } from '../instance/selectors';
import * as messageActions from '../messages/actions';
import { getTenant } from '../session/selectors';
import {
  hasInstances,
  areInstancesLoading,
  areInstancesUpdating,
  selectCachedInstances
} from './selectors';

const strings = {
  GENERIC_ERROR: 'Instances could not be loaded at this time.',
  NO_INSTANCES: 'You have no instances of Watson Assistant.'
};

export const loadInstances = (): AsyncAppThunk => {
  return async (dispatch, getState) => {
    if (hasInstances(getState()) || !_.isEmpty(selectCachedInstances())) {
      dispatch(listUpdatedInstances());
    } else {
      dispatch(listNewInstances());
    }
  };
};

// Async actions
export const listNewInstances = createAsyncThunk<
  Instances,
  void,
  CreateAsyncThunk
>('instances/listNew', async (_, { dispatch }) => {
  try {
    return await listInstances();
  } catch (error) {
    dispatch(
      messageActions.addApiErrorMessage({
        ...error.response,
        message: strings.GENERIC_ERROR,
        transactionId: error.transactionId
      })
    );

    throw error;
  }
});

export const listUpdatedInstances = createAsyncThunk<
  Instances,
  void,
  CreateAsyncThunk
>('instances/listUpdated', async _ => {
  try {
    return await listInstances();
  } catch (error) {
    throw error;
  }
});

/**
 * If there are no instances in the state and not loading, try to load the instances from cache.
 * If the current instance is not in the cached list, load them from the API
 * @returns {Function} a function that will be processed by redux as a thunk
 */
export const loadInstancesIfRequired = (): AsyncAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    if (!areInstancesLoading(state) && !hasInstances(state)) {
      // Get the current tenant from the session
      const tenant = getTenant(state);
      const instances = selectCachedInstances() || [];

      // Get the current instance from the cached instances list
      const currentInstance = instances.find(
        ({ region, instance_id }) =>
          region === tenant.region && instance_id === tenant.instance
      );

      // Get the current instance plan from limits
      const currentPlanId = selectPlanId(state);

      // If the current instance is loaded, and the plan id is up to date
      const hasCurrentInstance =
        currentInstance && currentInstance.plan.guid === currentPlanId;

      if (hasCurrentInstance) {
        return dispatch(
          listNewInstances.fulfilled(
            { resources: instances, inaccessibleAccountNames: [] },
            null,
            null
          )
        );
      } else {
        return dispatch(loadInstances());
      }
    }
  };
};

export const reloadInstancesIfRequired = (): AsyncAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    if (!areInstancesLoading(state) && !areInstancesUpdating(state)) {
      return dispatch(loadInstances());
    } else {
      return Promise.resolve();
    }
  };
};
